import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, CheckCircle, ArrowRight, Phone, Mail, MessageCircle,
  AlertTriangle, FileText, Clock, MapPin, Briefcase, Users, Building2,
  ChevronRight, XCircle, Plane, FileCheck, ClipboardList, BadgeCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';
import { useContactChannel } from '@/hooks/useContactChannel';

/**
 * /saudi-arabia/business-visa  (consolidated canonical, Feb 2026)
 * Legacy URL /saudi-business-visit-visa-for-indians 301s to this path via
 * /public/_redirects.
 * ────────────────────────────────────────────────────────────────────────
 * SEO landing page targeting "Saudi Business Visit Visa for Indians" and
 * related queries: Saudi business visa requirements India, Saudi business
 * visa processing time, multiple entry business visa Saudi, etc.
 *
 * Reflects 2026 policy reality: only single-entry business visa available
 * for Indian nationals (Feb 1, 2025 temporary restriction still active).
 */

const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const ELIGIBILITY = [
  { label: 'Valid Indian Passport', detail: 'Minimum 6 months validity beyond intended entry date, with at least 2 consecutive blank pages.' },
  { label: 'Age Requirement', detail: 'At least 18 years old (minors must accompany a legal guardian).' },
  { label: 'Clean Criminal Record', detail: 'No criminal convictions or pending cases in India or any country.' },
  { label: 'Valid Saudi Sponsorship', detail: 'Mandatory invitation from a registered Saudi company approved by MOFA.' },
  { label: 'Health Insurance', detail: 'Valid medical insurance recognised in Saudi Arabia (mandatory since 2024).' },
  { label: 'Financial Capability', detail: 'Sufficient funds to cover accommodation, food, and expenses during stay.' },
  { label: 'No Israeli Stamps', detail: 'Passport must not contain any Israeli visas or stamps.' },
];

const ALLOWED_ACTIVITIES = [
  'Attending business meetings with Saudi companies',
  'Participating in conferences, trade shows, and exhibitions',
  'Conducting site visits and project inspections',
  'Exploring investment opportunities and market research',
  'Negotiating contracts with clients or suppliers',
  'Attending corporate training programs and workshops',
  'Meeting with existing clients to maintain business relationships',
];

const NOT_ALLOWED = [
  { label: 'Employment / Work', detail: 'You cannot take up employment with a Saudi employer (requires Work Visa).' },
  { label: 'Tourism', detail: 'Not valid for leisure travel (requires Tourist Visa).' },
  { label: 'Medical Treatment', detail: 'Requires a separate Medical Visa.' },
  { label: 'Hajj / Umrah', detail: 'Religious pilgrimage requires an Umrah Visa.' },
  { label: 'Stay Beyond 30 Days', detail: 'Single-entry visa is limited to a 30-day maximum stay.' },
];

const DOCUMENT_GROUPS = [
  {
    icon: FileText,
    title: '1. Original Passport',
    points: [
      'Original Indian passport valid for minimum 6 months beyond intended travel date',
      'At least 2 consecutive blank pages for visa stamps',
      'Passport must be undamaged — no fraying, tears, or alterations',
      'Photocopies of data page, all visa/stamp pages, first and last pages',
      'Old passport (if available) with any previous Saudi visas',
    ],
  },
  {
    icon: BadgeCheck,
    title: '2. Saudi MOFA / Chamber of Commerce Invitation Letter (Wakala/Enjaz)',
    points: [
      'Original invitation letter on Saudi company letterhead',
      'MOFA-approved (Ministry of Foreign Affairs) with Chamber of Commerce attestation',
      'Dated within the last 30 days',
      'Must include your full name, passport number, purpose of visit, and duration of stay',
      'Sponsor guarantee with sponsor contact information, official stamp, and CR number',
      'For e-visa: submitted through the Muqeem or Enjaz platform',
    ],
  },
  {
    icon: ClipboardList,
    title: '3. Cover Letter from Indian Company',
    points: [
      'Printed on official company letterhead, addressed to Embassy of Saudi Arabia, Visa Section, New Delhi',
      'Original signature of a senior manager (Vice-President or equivalent above)',
      'Nature of your Indian company, your designation, and employment details',
      'Specific purpose of visit, Saudi companies you will meet (with contact names and phone numbers)',
      'Expected travel dates (entry/exit within the 30-day limit)',
      'Confirmation that your company bears responsibility for your stay, conduct, and return',
      'Must match the invitation letter exactly (dates, purpose, names)',
    ],
  },
  {
    icon: Building2,
    title: '4. Chamber of Commerce Recommendation Letter (India)',
    points: [
      'Issued on official MCCIA or relevant state chamber letterhead',
      'Sealed and signed by an authorised Chamber officer',
      'Validates the business purpose of your visit',
      'Confirms your designation and company registration',
      'Required for first-time business visa applicants',
    ],
  },
  {
    icon: Users,
    title: '5. Photographs (Strict Specifications)',
    points: [
      'Two recent colour passport photographs, taken within the last 6 months',
      'Size: 35mm × 45mm (approx. 2 inches × 2 inches)',
      'White background only — no patterns, gradients, or off-white',
      'Full frontal view with face centred (approx. 80% face coverage)',
      'Neutral expression, no smiling, no eyeglasses or headwear (except for religious reasons)',
      'Matte finish, high-quality photo paper — no adhesive tape or staples',
      'Digital version: 200×200 pixels, 5–100 KB for online applications',
    ],
  },
  {
    icon: Briefcase,
    title: '6. Employment Documentation',
    points: [
      'No-Objection Certificate (NOC) from your Indian employer',
      'Copy of employment contract or appointment letter',
      'Salary certificate or employment verification letter stating "business" as travel purpose',
      'Company registration certificate or business license (for self-employed)',
    ],
  },
  {
    icon: FileCheck,
    title: '7. Financial Documents',
    points: [
      'Bank statement for the last 3 months — original, signed, and stamped by the bank',
      'Printed on A4-sized paper with visible bank seal',
      'Demonstrates sufficient funds to support your 30-day stay',
      'Proof of employment or business income documents',
      'Tax returns (if self-employed)',
    ],
  },
  {
    icon: Plane,
    title: '8. Travel Documents',
    points: [
      'Confirmed round-trip flight tickets or booking confirmation',
      'Entry and exit dates from Saudi Arabia (within 30-day limit)',
      'Hotel accommodation booking in Saudi Arabia (confirmed)',
      'Travel itinerary showing dates and locations of business activities',
    ],
  },
  {
    icon: Shield,
    title: '9. Health Insurance',
    points: [
      'Valid health insurance policy recognised in Saudi Arabia',
      'Covers medical emergencies and hospitalisation for the entire 30-day stay',
      'Insurance document copy with policy number and coverage details',
    ],
  },
];

const APPLICATION_PHASES = [
  {
    phase: 'Phase 1 — Pre-Application Preparation',
    duration: '1–2 weeks',
    steps: [
      'Secure your Saudi MOFA Invitation Letter via your Saudi business partner through the Chamber of Commerce.',
      'Gather all required documents per the checklist above; ensure validity within 30 days.',
      'Attestation by Indian Chamber of Commerce (3–7 days) and professional translation if needed.',
      'Purchase mandatory travel health insurance valid in Saudi Arabia for the full 30-day stay.',
    ],
  },
  {
    phase: 'Phase 2 — Submit Application Through ZAFCO',
    duration: '1–2 days',
    steps: [
      'Contact ZAFCO for MEA-approved document review and eligibility verification.',
      'Document pickup from New Delhi or pan-India locations.',
      'Application package verified and submitted to the Royal Embassy of Saudi Arabia.',
    ],
  },
  {
    phase: 'Phase 3 — Embassy Processing & Biometrics',
    duration: '5–10 business days',
    steps: [
      'Biometric collection at VFS Global (mandatory for applicants aged 6–60).',
      'Application forwarded to the Royal Embassy of Saudi Arabia, New Delhi.',
      'Security clearance, background verification, and Commercial Wing vetting.',
    ],
  },
  {
    phase: 'Phase 4 — Visa Collection',
    duration: '1–2 days',
    steps: [
      'Passport with visa stamp collected from VFS — or courier delivery option.',
      'E-Visa: download printable confirmation from email.',
      'Verify all details before travel: name, passport number, validity dates, single-entry type.',
    ],
  },
];

const TIMELINE = [
  { phase: 'Pre-Application', activity: 'Document gathering & attestation', duration: '1 week', notes: 'Parallel with sponsor preparation' },
  { phase: 'Sponsor Submission', activity: 'MOFA invitation request', duration: '1 week', notes: 'Depends on sponsor efficiency' },
  { phase: 'Application Submission', activity: 'Document review & submission', duration: '1–2 days', notes: 'Through ZAFCO' },
  { phase: 'Biometrics', activity: 'VFS center visit', duration: '1 day', notes: 'Appointment-based, 15–30 min' },
  { phase: 'Embassy Processing', activity: 'Security clearance & verification', duration: '5–10 days', notes: 'Standard processing window' },
  { phase: 'Visa Collection', activity: 'Passport collection', duration: '1–2 days', notes: 'After approval notification' },
];

const WHY_ZAFCO = [
  { label: 'MEA-Approved Recruitment Agency', detail: `Government of India approved with RC: ${MEA_REG}` },
  { label: 'Saudi Embassy Empanelled', detail: 'Direct partnership with the Royal Embassy of Saudi Arabia for Visa Application and Passport Submission.' },
  { label: '15+ Years Experience', detail: 'Over 10,000 successful placements across 50+ countries.' },
  { label: '98% Success Rate', detail: 'Processing 40,000+ visas with minimal rejection.' },
  { label: 'Deep Saudi Expertise', detail: '15+ years specialising in Saudi corporate mobility and business visa processing.' },
  { label: 'Unmatched Documentation Precision', detail: 'Experts verify EVERY document to prevent rejection due to incomplete paperwork.' },
  { label: 'Premium Client-Centric Service', detail: 'Personalised guidance from assessment to visa collection.' },
  { label: 'End-to-End Support', detail: 'From invitation letter coordination to visa collection — we handle everything.' },
];

const FAQS = [
  {
    q: 'Can Indian nationals apply for a multiple-entry business visa to Saudi Arabia in 2026?',
    a: "No. As of February 1, 2025, Saudi Arabia temporarily suspended multiple-entry business visas for nationals of 14 countries including India. Only single-entry business visas are available until further notice.",
  },
  {
    q: 'How long does the Saudi business visa processing time in India take for single-entry visas?',
    a: 'Standard embassy processing is 5–10 business days. Total duration from start (document gathering) to visa in hand is approximately 3–4 weeks. Document attestation runs in parallel and takes 3–7 working days.',
  },
  {
    q: 'What are the Saudi business visa requirements for Indian citizens in 2026?',
    a: 'Essential documents: valid Indian passport (6+ months validity, 2 blank pages), MOFA-approved Saudi invitation letter (Wakala/Enjaz), cover letter from your Indian company on letterhead, Chamber of Commerce recommendation letter, two passport photos (35×45mm, white background), 3-month bank statement (stamped), confirmed flight tickets and hotel booking, and valid health insurance for Saudi Arabia.',
  },
  {
    q: 'Can I apply for a Saudi business visa online from India?',
    a: 'Yes, for e-visa. If your Saudi sponsor submits the invitation through the Muqeem or Enjaz platform, you can complete the entire application online without visiting VFS. For traditional visa, documents are submitted through an authorised agency (like ZAFCO) and biometrics are captured at VFS.',
  },
  {
    q: 'What is the validity and duration of stay for a Saudi single-entry business visa for Indians?',
    a: 'Visa validity: 90 days from the date of issuance. Duration of stay: up to 30 days per visit. Entries: 1 entry only (no re-entry permitted). Extensions beyond 30 days are not available for Indian nationals under current restrictions.',
  },
  {
    q: 'Can I work in Saudi Arabia on a Business Visit Visa?',
    a: 'No. The Business Visit Visa is strictly for business activities (meetings, conferences, site visits) without employment. Taking up work requires a Work Visa with different requirements. Working on a business visa violates visa terms and can result in deportation and future visa bans.',
  },
  {
    q: 'Is health insurance mandatory for a Saudi Business Visa in 2026?',
    a: 'Yes. Valid health insurance recognised in Saudi Arabia is mandatory for visa approval. This requirement has been strictly enforced since 2024 and continues in 2026. Insurance must cover medical emergencies and hospitalisation for the entire 30-day stay.',
  },
  {
    q: 'Can I extend my Saudi Business Visit Visa while in Saudi Arabia?',
    a: 'Generally no for Indian nationals. Under current temporary restrictions (February 2025), business visa extensions beyond 30 days are not available for Indian nationals. You must exit Saudi Arabia within 30 days and apply for a new single-entry visa for subsequent visits.',
  },
];

const SaudiBusinessVisitVisaPage = () => {
  const ch = useContactChannel();

  usePageMeta(
    'Saudi Business Visit Visa for Indians 2026 | MEA-Approved | ZAFCO',
    'Saudi Business Visit Visa for Indians — complete 2026 guide. Single-entry visa eligibility, document checklist, processing time. MEA-approved & Saudi Embassy empanelled.',
    {
      canonical: '/saudi-arabia/business-visa',
      image: '/social-media/zafco-facebook-cover.png',
      imageAlt: 'Saudi Business Visit Visa for Indians — ZAFCO MEA-approved Saudi visa expert in New Delhi.',
    },
  );

  // FAQ schema (one per page, with cleanup) — Google Rich Results eligibility
  useFAQSchema(FAQS);

  // Breadcrumb + Service JSON-LD
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Saudi Arabia', item: 'https://zafco.in/saudi-arabia' },
        {
          '@type': 'ListItem', position: 3,
          name: 'Saudi Business Visit Visa for Indians',
          item: 'https://zafco.in/saudi-arabia/business-visa',
        },
      ],
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Saudi Business Visit Visa Processing for Indians',
      description: 'MEA-approved Saudi Business Visit Visa processing for Indian nationals — document verification, MOFA invitation coordination, VFS biometrics, embassy submission.',
      serviceType: 'Visa Processing',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'India' },
        { '@type': 'Country', name: 'Saudi Arabia' },
      ],
      audience: { '@type': 'Audience', audienceType: 'Indian business professionals travelling to Saudi Arabia' },
      url: 'https://zafco.in/saudi-arabia/business-visa',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-saudi-bvv-page', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  const contactQuery = '?source=saudi-business-visa';
  const waMessage = 'Hello ZAFCO, I want to apply for a Saudi Business Visit Visa from India. Please share next steps.';

  return (
    <div data-testid="saudi-business-visit-visa-page">
      <PageHeader
        title="Saudi Business Visit Visa for Indians — 2026 Guide"
        subtitle="MEA-approved single-entry business visa processing for Indian professionals. End-to-end documentation, MOFA invitation coordination, and Royal Saudi Embassy submission by ZAFCO."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Saudi Arabia', path: '/saudi-arabia' },
          { label: 'Saudi Business Visit Visa for Indians', path: '/saudi-arabia/business-visa' },
        ]}
      />

      {/* Government Approval Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved · Royal Saudi Embassy Empanelled</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* SECTION 1 — Introduction & Primary CTAs */}
      <section className="py-16 bg-white" aria-labelledby="saudi-bvv-intro">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Saudi Corporate Mobility · 2026</span>
          <h2 id="saudi-bvv-intro" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            India&rsquo;s premier business destination — process your Saudi visa with ZAFCO
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Kingdom of Saudi Arabia has transformed into one of the world&rsquo;s most dynamic business hubs, driven by Vision 2030. Over <strong>100,000 Indian business professionals</strong> visit Saudi Arabia annually for meetings, conferences, trade shows, and investment exploration.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            The <strong>Saudi Business Visit Visa for Indians</strong> is the official travel document issued by Saudi MOFA (Ministry of Foreign Affairs) that enables Indian nationals to enter Saudi Arabia for legitimate business purposes — <em>without seeking employment</em>. As of <strong>2026</strong>, Indian citizens can access the <strong>Single-Entry Business Visa</strong> with streamlined processing through the Enjaz/Muqeem platforms and the Royal Embassy of Saudi Arabia, New Delhi.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link> is your MEA-approved partner for Saudi corporate mobility — 15+ years of expertise, direct Royal Saudi Embassy empanelment, and a 98% visa success rate.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            For the Kingdom&rsquo;s complete corporate compliance picture — Commercial vs. Working Visit boundaries, MOFA/MISA LOI workflows, and sponsor-free residency channels — refer to the{' '}
            <Link to="/services/saudi-business-visit-visa/" data-testid="old-biz-link-new" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">2026 Commercial Business Visit & Premium Residency Rules</Link>.
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-3 mt-10">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="saudi-bvv-hero-cta-apply">
              <Link to={`/contact${contactQuery}&intent=apply`}>
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Apply for Saudi Business Visa
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue/5" data-testid="saudi-bvv-hero-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" aria-hidden="true" /> WhatsApp Us
              </a>
            </Button>
            <Button asChild variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="saudi-bvv-hero-cta-call">
              <a href={ch.phoneTelUrl}>
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" /> Call {ch.phoneDisplay}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Critical 2026 Update (Single-Entry Restriction) */}
      <section className="py-16 bg-amber-50 border-y border-amber-200" aria-labelledby="saudi-bvv-update">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex items-start gap-3 mb-6">
            <AlertTriangle className="w-8 h-8 text-amber-700 flex-shrink-0 mt-1" aria-hidden="true" />
            <div>
              <span className="text-amber-700 font-semibold text-sm tracking-widest uppercase">Critical 2026 Update</span>
              <h2 id="saudi-bvv-update" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-1">
                Single-Entry Visa Only for Indian Nationals
              </h2>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Important Notice:</strong> As of February 1, 2025, Saudi Arabia implemented temporary visa restrictions affecting 14 countries including India. This policy remains active in <strong>2026</strong>.
          </p>

          <div className="overflow-x-auto rounded-xl border border-amber-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-amber-100 text-zafco-blue">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Visa Type</th>
                  <th className="px-4 py-3 text-left font-bold">Current Status for Indians</th>
                  <th className="px-4 py-3 text-left font-bold">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                <tr>
                  <td className="px-4 py-3 font-semibold">Single-Entry Business Visa</td>
                  <td className="px-4 py-3 text-green-700 font-semibold">✅ Available</td>
                  <td className="px-4 py-3 text-gray-700">Valid 90 days from issuance, up to 30 days stay</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Multiple-Entry (1 Year)</td>
                  <td className="px-4 py-3 text-red-700 font-semibold">❌ Temporarily Suspended</td>
                  <td className="px-4 py-3 text-gray-700">Not available until further notice</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-semibold">Multiple-Entry (5 Years)</td>
                  <td className="px-4 py-3 text-red-700 font-semibold">❌ Temporarily Suspended</td>
                  <td className="px-4 py-3 text-gray-700">Not available until further notice</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="bg-white rounded-xl border border-amber-200 p-5">
              <h3 className="font-bold text-zafco-blue mb-3">What this means for your business travel</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Only single-entry business visa currently available</li>
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Maximum stay: 30 days per visit</li>
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Visa validity: 90 days from date of issuance</li>
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> No conversion to work visa while in Saudi Arabia</li>
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Diplomatic, Hajj, Umrah, residence visas unaffected</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl border border-amber-200 p-5">
              <h3 className="font-bold text-zafco-blue mb-3">Why was this restriction implemented?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Visa misuse during Hajj season</li>
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Undocumented pilgrims obtaining long-term visit visas</li>
                <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Unauthorised labour migration through business channels</li>
              </ul>
              <p className="mt-3 text-xs text-gray-600 italic">Authorities describe this as a temporary measure with no promised date for reconsideration. Business travellers must plan accordingly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Eligibility Criteria */}
      <section className="py-16 bg-white" aria-labelledby="saudi-bvv-eligibility">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Eligibility</span>
          <h2 id="saudi-bvv-eligibility" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Who can apply for a Saudi Business Visit Visa from India?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            To qualify for a Saudi Business Visit Visa, Indian applicants must meet all of the following primary requirements:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {ELIGIBILITY.map((item) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-5"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <h3 className="font-bold text-zafco-blue mb-1">{item.label}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Allowed activities */}
          <h3 className="text-2xl font-bold text-zafco-blue font-syne mb-4">Acceptable business activities</h3>
          <p className="text-gray-700 mb-4">The visa is strictly for business purposes — <em>not</em> employment. You can use it for:</p>
          <ul className="grid sm:grid-cols-2 gap-3 mb-12 text-gray-700">
            {ALLOWED_ACTIVITIES.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>

          {/* Not allowed */}
          <h3 className="text-2xl font-bold text-zafco-blue font-syne mb-4">What this visa does NOT cover</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {NOT_ALLOWED.map((item) => (
              <div key={item.label} className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <h4 className="font-semibold text-red-800">{item.label}</h4>
                  <p className="text-sm text-red-700">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — Document Checklist */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-bvv-documents">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Document Checklist</span>
          <h2 id="saudi-bvv-documents" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Saudi business visa requirements for Indian citizens
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Complete, attested documentation is the #1 driver of visa approval. ZAFCO experts verify every document before embassy submission.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {DOCUMENT_GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-zafco-blue/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-zafco-blue" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-zafco-blue text-base">{group.title}</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {group.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-10 bg-zafco-blue/5 border border-zafco-blue/20 rounded-xl p-5">
            <h3 className="font-bold text-zafco-blue mb-2">Document attestation & translation</h3>
            <ul className="space-y-1.5 text-sm text-gray-700">
              <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> All documents issued in India must be attested by the Indian Chamber of Commerce or relevant state authority</li>
              <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Documents not in English or Arabic must be translated by a certified translator</li>
              <li className="flex items-start gap-2"><ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> Translated documents must also be attested</li>
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Step-by-Step Process */}
      <section className="py-16 bg-white" aria-labelledby="saudi-bvv-process">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Application Process</span>
          <h2 id="saudi-bvv-process" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-8">
            Process your Saudi Business Visa seamlessly with ZAFCO
          </h2>

          <div className="space-y-5">
            {APPLICATION_PHASES.map((p, idx) => (
              <div key={p.phase} className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-zafco-blue text-white flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </div>
                    <h3 className="font-bold text-zafco-blue text-lg">{p.phase}</h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {p.duration}
                  </span>
                </div>
                <ul className="space-y-2 text-sm text-gray-700">
                  {p.steps.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — Processing Timeline */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-bvv-timeline">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Timeline</span>
          <h2 id="saudi-bvv-timeline" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Saudi business visa processing time in India — 2026
          </h2>
          <p className="text-gray-700 mb-8">Standard processing window for single-entry business visa, end-to-end.</p>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Phase</th>
                  <th className="px-4 py-3 text-left font-bold">Activity</th>
                  <th className="px-4 py-3 text-left font-bold">Duration</th>
                  <th className="px-4 py-3 text-left font-bold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {TIMELINE.map((row) => (
                  <tr key={row.phase}>
                    <td className="px-4 py-3 font-semibold text-zafco-blue">{row.phase}</td>
                    <td className="px-4 py-3 text-gray-700">{row.activity}</td>
                    <td className="px-4 py-3 text-gray-700">{row.duration}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{row.notes}</td>
                  </tr>
                ))}
                <tr className="bg-amber-50 font-bold">
                  <td className="px-4 py-3 text-zafco-blue">TOTAL</td>
                  <td className="px-4 py-3 text-gray-700">Start to visa in hand</td>
                  <td className="px-4 py-3 text-amber-700">2–3 weeks</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">Subject to sponsor & embassy timelines</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-600 mt-4 italic">
            Contact our experts for current express-processing availability and timeline updates.
          </p>
        </div>
      </section>

      {/* SECTION 7 — Why ZAFCO */}
      <section className="py-16 bg-white" aria-labelledby="saudi-bvv-why-zafco">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Why ZAFCO</span>
          <h2 id="saudi-bvv-why-zafco" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Government of India–approved Saudi visa expert
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            ZAFCO Human Resource Management is the trusted authority for Saudi business visas in India — directly empanelled with the Royal Saudi Embassy.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {WHY_ZAFCO.map((item) => (
              <div key={item.label} className="flex items-start gap-3 bg-slate-50 rounded-xl border border-slate-200 p-5">
                <BadgeCheck className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <h3 className="font-bold text-zafco-blue">{item.label}</h3>
                  <p className="text-sm text-gray-700">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-gradient-to-r from-zafco-blue to-zafco-blue/90 text-white p-6 md:p-8">
            <div className="grid md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <h3 className="font-syne font-bold text-xl mb-2">Visit our New Delhi office</h3>
                <p className="text-white/90 text-sm leading-relaxed flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  GF 5, 27B, Khizrabad, New Friends Colony, New Delhi 110025, India
                </p>
                <p className="text-white/90 text-sm leading-relaxed flex items-start gap-2 mt-2">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <a href={ch.phoneTelUrl} className="hover:underline">{ch.phoneDisplay}</a>
                </p>
                <p className="text-white/90 text-sm leading-relaxed flex items-start gap-2 mt-2">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <a href={`mailto:${ch.email}`} className="hover:underline">{ch.email}</a>
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Button asChild className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold" data-testid="saudi-bvv-office-cta-apply">
                  <Link to={`/contact${contactQuery}&intent=apply`}>Apply Now <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
                <Button asChild variant="outline" className="bg-white text-zafco-blue border-white hover:bg-white/90" data-testid="saudi-bvv-office-cta-whatsapp">
                  <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Consultation Form (Conversion) */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-bvv-consultation">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Get Started</span>
              <h2 id="saudi-bvv-consultation" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
                Start your Saudi Business Visa application today
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Incomplete documentation is the #1 reason for visa rejection. ZAFCO&rsquo;s MEA-approved experts review every document before submission to ensure 100% compliance with MOFA guidelines and current 2026 restrictions.
              </p>
              <ul className="space-y-2 text-sm text-gray-700 mb-6">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Instant consultation with an MEA-approved executive</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Personalised eligibility review for single-entry restriction</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Transparent, up-to-date pricing — no hidden charges</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Hassle-free document pickup across New Delhi & pan-India</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Application submitted within 24–48 hours</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-theme="light">
              <ConsultationForm
                source="saudi-business-visa"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 — FAQs */}
      <section className="py-16 bg-white" aria-labelledby="saudi-bvv-faqs">
        <div className="container mx-auto px-6 max-w-4xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">FAQs</span>
          <h2 id="saudi-bvv-faqs" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-8">
            Frequently asked questions — Saudi Business Visa for Indians
          </h2>
          <div className="space-y-3">
            {FAQS.map((item, idx) => (
              <details
                key={item.q}
                className="group rounded-xl border border-slate-200 bg-slate-50 open:bg-white open:shadow-sm transition-all"
                data-testid={`saudi-bvv-faq-${idx}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 p-5 font-semibold text-zafco-blue">
                  <span className="flex-1">{item.q}</span>
                  <ChevronRight className="w-5 h-5 flex-shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
                </summary>
                <div className="px-5 pb-5 text-sm text-gray-700 leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10 — Final CTA */}
      <section className="py-16 bg-zafco-blue text-white" aria-labelledby="saudi-bvv-final-cta">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 id="saudi-bvv-final-cta" className="text-3xl md:text-4xl font-bold font-syne mb-4">
            Ready to travel to Saudi Arabia for business?
          </h2>
          <p className="text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto">
            Navigate 2026&rsquo;s single-entry visa restrictions with ZAFCO&rsquo;s MEA-approved expertise — 98% success rate and direct Royal Saudi Embassy empanelment.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold" data-testid="saudi-bvv-final-cta-apply">
              <Link to={`/contact${contactQuery}&intent=apply`}>
                Apply for Saudi Visa <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="bg-white text-zafco-blue border-white hover:bg-white/90" data-testid="saudi-bvv-final-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp {ch.whatsappDisplay}
              </a>
            </Button>
            <Button asChild variant="outline" className="bg-transparent text-white border-white hover:bg-white/10" data-testid="saudi-bvv-final-cta-call">
              <a href={ch.phoneTelUrl}>
                <Phone className="w-4 h-4 mr-2" /> Call {ch.phoneDisplay}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SaudiBusinessVisitVisaPage;
